import type { SortDirection } from '@core/search-types';
import type { UnitPrecision } from '@core/utils-legacy';

import { ReportDateFilter } from './common.types';
import {
  ReportDashboardComponentChartType,
  ReportDashboardComponentKind,
  ReportDashboardComponentViewMode,
  ReportDashboardUnavailabilityReason,
} from './consts.types';
import { TimeBasedMetricDatePoint } from './metrics.types';

export interface ReportDashboardComponentGridPosition {
  cols: number;
  rows: number;
  x: number;
  y: number;
}

export interface ReportDashboardComponentSettings {
  interval?: 'daily' | 'weekly' | 'monthly' | 'quarterly';
  amountOfDataDisplayed?: number;
  localSortBy?: string | null;
  localSortOrder?: SortDirection | null;
}

export type ReportDashboardComponentOptionsKeys =
  | 'includeDeletedCandidates'
  | 'includeArchivedJobs'
  | 'showAllData'
  | 'includeArchivedRequisitions';

export type ReportDashboardComponentOptions = {
  [key in ReportDashboardComponentOptionsKeys]?: boolean;
};

export type ReportDashboardComponentOrigin = 'blank' | 'template' | 'duplicate' | null;

export type ReportParamFilter = { group: string; value: string; inverse?: boolean };
export type ReportParamFilters = ReportParamFilter[];

interface ReportDashboardComponentViewDataBase {
  templateId: string | null;
  isAvailable: boolean;
  unavailableReason: null | ReportDashboardUnavailabilityReason;
  name: string;
  description: string | null;
  chartType: ReportDashboardComponentChartType | null;
  metric: string;
  grid: ReportDashboardComponentGridPosition;
  sortBy: string | null;
  sortOrder: SortDirection | null;
  settings?: ReportDashboardComponentSettings;
  filters: ReportParamFilters;
  primaryGroup: string | null;
  secondaryGroup: string | null;
  dateFilter: ReportDateFilter;
  dateField: {
    field: string;
    resource: string;
  };
  options: ReportDashboardComponentOptions;
  viewMode: ReportDashboardComponentViewMode;
  origin: ReportDashboardComponentOrigin;
  generated?: boolean;
}
export interface ReportDashboardComponentViewDataNonTimeBased
  extends ReportDashboardComponentViewDataBase {
  kind: Exclude<ReportDashboardComponentKind, ReportDashboardComponentKind.TimeBased>;
}

export interface ReportDashboardComponentViewDataTimeBased
  extends ReportDashboardComponentViewDataBase {
  kind: ReportDashboardComponentKind.TimeBased;
  startPoint: TimeBasedMetricDatePoint['key'] | null;
  endPoint: TimeBasedMetricDatePoint['key'] | null;
}

export type ReportDashboardComponentViewData =
  | ReportDashboardComponentViewDataNonTimeBased
  | ReportDashboardComponentViewDataTimeBased;

export type ReportDashboardComponent = ReportDashboardComponentViewData & {
  id: number | string;
  localId: string;
};

export interface ReportDashboardComponentMetaResponse {
  dateRange?: string;
  dateStart?: string;
  dateEnd?: string;
  unitPrecision?: UnitPrecision;
}

export interface ReportVisualizationDataResponse {
  checksum: string;
  meta: ReportDashboardComponentMetaResponse;
  results: {
    columns: Array<{ name: string; id: number | string }>;
    rows: Array<{
      category?: string;
      name: string;
      id: string | number;
      values: number[];
    }>;
  };
}

export enum ReportComponentActionIntent {
  Edit = 'edit',
  Fullscreen = 'fullscreen',
  Duplicate = 'duplicate',
  Move = 'move',
  Delete = 'delete',
  ExportCsv = 'exportCsv',
  ExportPng = 'exportPng',
  ExportXlsx = 'exportXlsx',
  ExportPdf = 'exportPdf',
}

export const REPORT_COMPONENT_DRAG_HANDLE_CLASS = 'drag-handle';
